import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { DestroyRef, inject, Injectable, PLATFORM_ID } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { catchError, of, skip, Subject } from "rxjs";
import { MarketingApiService } from "./marketing-api.service";
import { MarketingMeasurementService } from "./measurement.service";
import {
  hasMarketingEvidence,
  MarketingEntry,
  parseMarketingEntry,
  hasUnsafeMarketingText,
} from "./marketing-context";

interface StoredContext {
  context_token: string;
  event_id: number;
  expires_at: string;
  session_token: string;
  consent_receipt: string;
}
@Injectable({ providedIn: "root" })
export class AttributionContextService {
  readonly ready = new Subject<number>();
  private readonly api = inject(MarketingApiService);
  private readonly measurement = inject(MarketingMeasurementService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly initialPath = this.document.location?.pathname;
  private referrerConsumed = false;
  private contexts: Record<string, StoredContext> = {};
  private homeEntry?: { entry: MarketingEntry; expires_at: number };
  private readonly inFlight = new Set<string>();
  private readonly captures = new Map<string, string>();
  private lastEvent?: number;
  private generation = 0;
  private restored = false;
  private readonly waiting = new Map<number, MarketingEntry>();
  constructor() {
    if (this.measurement.trackingReceipt) this.restore();
    this.measurement.changes
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.generation++;
        this.contexts = {};
        this.captures.clear();
        if (!this.measurement.trackingReceipt) {
          this.waiting.clear();
          this.remove("yes7-marketing-contexts");
          return;
        }
        if (!this.restored) this.restore();
        else this.remove("yes7-marketing-contexts");
        this.observeNavigation();
        const waiting = [...this.waiting.entries()];
        this.waiting.clear();
        for (const [eventId, entry] of waiting) this.capture(eventId, entry);
        if (!waiting.length && this.lastEvent) this.eventLoaded(this.lastEvent);
      });
  }
  private restore(): void {
    this.restored = true;
    if (this.browser && this.measurement.trackingReceipt) {
      const stored = this.read<Record<string, StoredContext>>(
        "yes7-marketing-contexts",
      );
      if (stored && typeof stored === "object" && !Array.isArray(stored))
        for (const [id, value] of Object.entries(stored)) {
          if (
            /^\d+$/.test(id) &&
            value &&
            value.event_id === Number(id) &&
            typeof value.context_token === "string" &&
            /^[A-Za-z0-9_-]{1,128}$/.test(value.context_token) &&
            typeof value.session_token === "string" &&
            /^[0-9a-f-]{36}$/i.test(value.session_token) &&
            value.consent_receipt === this.measurement.trackingReceipt &&
            typeof value.expires_at === "string" &&
            Date.parse(value.expires_at) > Date.now()
          )
            this.contexts[id] = value;
        }
      const home = this.read<{ entry: MarketingEntry; expires_at: number }>(
        "yes7-marketing-home",
      );
      if (
        home &&
        home.expires_at > Date.now() &&
        home.entry &&
        typeof home.entry.utm === "object" &&
        Array.isArray(home.entry.click_identifiers)
      )
        this.homeEntry ||= home;
    }
  }
  observeNavigation(): void {
    if (this.browser) this.measurement.ensure();
    if (
      !this.browser ||
      (!this.measurement.analytics &&
        !this.measurement.advertising &&
        !this.measurement.pending)
    )
      return;
    const entry = this.entry();
    if (this.measurement.trackingReceipt) this.session();
    if (entry.landing_path === "/" && hasMarketingEvidence(entry)) {
      this.homeEntry = { entry, expires_at: Date.now() + 30 * 60 * 1000 };
      if (this.measurement.trackingReceipt)
        this.write("yes7-marketing-home", this.homeEntry);
    }
  }
  eventLoaded(eventId: number): void {
    this.lastEvent = eventId;
    if (
      !this.browser ||
      !Number.isSafeInteger(eventId) ||
      eventId <= 0 ||
      this.document.location.pathname !== `/event/${eventId}`
    )
      return;
    this.measurement.ensure();
    let entry = this.entry();
    this.referrerConsumed = true;
    // A home entry qualifies once, not for every event belonging to any producer.
    if (
      !hasMarketingEvidence(entry) &&
      this.homeEntry?.expires_at &&
      this.homeEntry.expires_at > Date.now()
    )
      entry = this.homeEntry.entry;
    this.homeEntry = undefined;
    if (this.measurement.trackingReceipt) this.remove("yes7-marketing-home");
    if (!this.measurement.trackingReceipt) {
      if (this.measurement.pending && this.waiting.size < 100)
        this.waiting.set(eventId, entry);
      return;
    }
    this.capture(eventId, entry);
  }
  private capture(eventId: number, entry: MarketingEntry): void {
    const existing = this.contexts[String(eventId)];
    const sessionToken = this.session();
    if (
      !hasMarketingEvidence(entry) &&
      existing &&
      existing.session_token === sessionToken &&
      Date.parse(existing.expires_at) > Date.now()
    ) {
      this.ready.next(eventId);
      return;
    }
    // The attribution window is not a browser-session window. A direct return
    // gets a fresh session touch; the server retains qualifying non-direct history.
    const receipt = this.measurement.trackingReceipt;
    if (!receipt) return;
    const generation = this.generation;
    const key = `${receipt}:${sessionToken}:${eventId}:${JSON.stringify(entry)}`;
    if (existing && this.captures.has(key)) {
      this.ready.next(eventId);
      return;
    }
    delete this.contexts[String(eventId)];
    if (this.inFlight.has(key)) return;
    this.inFlight.add(key);
    if (!this.captures.has(key)) this.captures.set(key, crypto.randomUUID());
    const matching: Record<string, string> = {};
    if (this.measurement.advertising) {
      for (const [field, cookie] of [
        ["fbp", "_fbp"],
        ["fbc", "_fbc"],
        ["ttp", "_ttp"],
      ]) {
        const value = this.document.cookie
          .split("; ")
          .find((c) => c.startsWith(`${cookie}=`))
          ?.slice(cookie.length + 1);
        if (value && value.length <= 2048 && !hasUnsafeMarketingText(value))
          matching[field] = value;
      }
    }
    const sourceUrl = this.measurement.advertising
      ? `${this.document.location.origin}/event/${eventId}`
      : undefined;
    this.api
      .context({
        ...entry,
        event_id: eventId,
        capture_id: this.captures.get(key),
        schema_version: 1,
        session_token: sessionToken,
        consent_receipt: receipt,
        matching,
        source_url: sourceUrl,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.inFlight.delete(key);
        if (
          !response ||
          response.event_id !== eventId ||
          typeof response.context_token !== "string" ||
          !/^[A-Za-z0-9_-]{1,128}$/.test(response.context_token) ||
          !Number.isFinite(Date.parse(response.expires_at)) ||
          Date.parse(response.expires_at) <= Date.now() ||
          this.measurement.trackingReceipt !== receipt ||
          generation !== this.generation ||
          (!this.measurement.analytics && !this.measurement.advertising)
        )
          return;
        this.contexts[String(eventId)] = {
          ...response,
          session_token: sessionToken,
          consent_receipt: receipt,
        };
        this.write("yes7-marketing-contexts", this.contexts);
        this.ready.next(eventId);
      });
  }
  leaveEvent(): void {
    this.lastEvent = undefined;
  }
  tokenFor(eventId: number): string | undefined {
    return this.paymentParameters().marketing_context_tokens?.[String(eventId)];
  }
  paymentParameters(): {
    marketing_context_tokens?: Record<string, string>;
    marketing_consent_receipt?: string;
  } {
    const receipt = this.measurement.trackingReceipt;
    if (!receipt) return {};
    const tokens: Record<string, string> = {};
    if (this.measurement.analytics || this.measurement.advertising)
      for (const [eventId, context] of Object.entries(this.contexts)) {
        if (Date.parse(context.expires_at) > Date.now())
          tokens[eventId] = context.context_token;
      }
    return {
      marketing_context_tokens: tokens,
      marketing_consent_receipt: receipt,
    };
  }
  private entry() {
    return parseMarketingEntry(
      this.document.location.href,
      this.document.location.pathname === this.initialPath &&
        !this.referrerConsumed
        ? this.document.referrer
        : "",
      this.measurement.advertising || this.measurement.pending,
    );
  }
  private session(): string {
    const current = this.read<{ token: string; last_seen: number }>(
      "yes7-marketing-session",
    );
    const token =
      current &&
      current.last_seen > Date.now() - 30 * 60 * 1000 &&
      current.last_seen <= Date.now() &&
      typeof current.token === "string" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        current.token,
      )
        ? current.token
        : crypto.randomUUID();
    this.write("yes7-marketing-session", { token, last_seen: Date.now() });
    return token;
  }
  private read<T>(key: string): T | null {
    try {
      return JSON.parse(localStorage.getItem(key) || "null") as T | null;
    } catch {
      return null;
    }
  }
  private write(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* Storage failure never blocks checkout. */
    }
  }
  private remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* Private browser */
    }
  }
}
